/* eslint-disable react/prop-types */
import ArgonBox from "components/ArgonBox";
import ArgonButton from "components/ArgonButton";
import ArgonBadge from "components/ArgonBadge";
import ArgonTypography from "components/ArgonTypography";
import ArgonProgress from "components/ArgonProgress";
import VisibilityIcon from "@mui/icons-material/Visibility";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { Link } from "react-router-dom";

const formatDate = (value) => {
  if (!value) {
    return "N/A";
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "N/A";
  }

  return date.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const getTutorInfo = (attendance, group) => {
  const tutorCandidate = attendance?.tutor || group.tutor;
  if (tutorCandidate) {
    return {
      name: tutorCandidate.name || tutorCandidate.fullName || "N/A",
      email: tutorCandidate.email || "",
    };
  }

  const sessionTutor = (group.classSession?.tutors || [])[0];
  if (sessionTutor) {
    return {
      name: sessionTutor.name || sessionTutor.fullName || "N/A",
      email: sessionTutor.email || "",
    };
  }

  return { name: "N/A", email: "" };
};

const getStudentsLabel = (students = []) => {
  if (!students.length) {
    return "N/A";
  }

  if (students.length === 1) {
    return students[0].name || "N/A";
  }

  return `${students.length} Students`;
};

const calculateProgress = (group) => {
  const progress = group.classProgress || {};
  const completedFromProgress = progress.completedClasses ?? group.attendances.length;
  const totalFromProgress =
    progress.totalClasses ?? Math.max(completedFromProgress, group.attendances.length);
  const remainingFromProgress =
    progress.remainingClasses ?? Math.max(0, totalFromProgress - completedFromProgress);

  return {
    totalClasses: totalFromProgress,
    completedClasses: completedFromProgress,
    remainingClasses: remainingFromProgress,
  };
};

const getStatusColor = (status) => {
  switch (status) {
    case "COMPLETED":
      return "success";
    case "ONGOING":
    case "SCHEDULED":
      return "info";
    case "ON_HOLD":
      return "warning";
    case "CANCELLED":
      return "error";
    default:
      return "secondary";
  }
};

const getPaymentColor = (paymentStatus) => {
  switch (paymentStatus) {
    case "COMPLETED":
      return "success";
    case "OVERDUE":
    case "FAILED":
      return "error";
    case "IN_PROGRESS":
      return "info";
    default:
      return "warning";
  }
};

function RemainingProgress({ percentage }) {
  return (
    <ArgonBox position="relative" width="120px" height="16px">
      <ArgonBox
        width="100%"
        height="100%"
        bgColor="grey-200"
        borderRadius="lg"
        position="relative"
        overflow="hidden"
      >
        <ArgonBox
          width={`${percentage}%`}
          height="100%"
          bgColor="primary"
          borderRadius="lg"
          position="absolute"
          top={0}
          left={0}
        />
        <ArgonBox
          position="absolute"
          top="50%"
          left="50%"
          sx={{ transform: "translate(-50%, -50%)" }}
        >
          <ArgonTypography variant="caption" fontWeight="medium" color="white">
            {percentage}%
          </ArgonTypography>
        </ArgonBox>
      </ArgonBox>
    </ArgonBox>
  );
}

const AttendanceTableData = (handleDeleteClick, handleEditClick, attendanceData = []) => {
  const groupedSessions = attendanceData.reduce((accumulator, attendance) => {
    const session = attendance.classSession || {};
    const sessionId =
      session.id || session._id || attendance.classSessionId || attendance.classSessionId?._id;

    if (!sessionId) {
      return accumulator;
    }

    if (!accumulator[sessionId]) {
      accumulator[sessionId] = {
        classSession: session,
        attendances: [],
        classProgress: attendance.classProgress || null,
        tutor: attendance.tutor || null,
      };
    }

    accumulator[sessionId].attendances.push(attendance);

    if (!accumulator[sessionId].classProgress && attendance.classProgress) {
      accumulator[sessionId].classProgress = attendance.classProgress;
    }

    if (!accumulator[sessionId].tutor && attendance.tutor) {
      accumulator[sessionId].tutor = attendance.tutor;
    }

    return accumulator;
  }, {});

  const rows = Object.values(groupedSessions).map((group, index) => {
    const latestAttendance = group.attendances.reduce((latest, current) => {
      if (!current.attendanceTime) {
        return latest;
      }

      if (!latest) {
        return current;
      }

      return new Date(current.attendanceTime) > new Date(latest.attendanceTime) ? current : latest;
    }, null);

    const topicCovered = latestAttendance?.topicCovered || "Class Session";
    const attendanceDate = formatDate(latestAttendance?.attendanceTime);
    const classSession = group.classSession || {};
    const tutorInfo = getTutorInfo(latestAttendance, group);
    const students = classSession.students || [];
    const progress = calculateProgress(group);
    const remainingPercentage =
      progress.totalClasses > 0
        ? Math.round((progress.remainingClasses / progress.totalClasses) * 100)
        : 0;
    const status = classSession.status || "CREATED";
    const paymentStatus = classSession.paymentStatus || "PENDING";
    const sessionId = classSession.id || classSession._id || null;

    return {
      sno: (
        <ArgonTypography variant="caption" color="text" fontWeight="medium">
          {index + 1}
        </ArgonTypography>
      ),
      Class: (
        <ArgonTypography
          variant="caption"
          component={sessionId ? Link : "span"}
          to={sessionId ? `/attendance/profile/${sessionId}` : undefined}
          color={sessionId ? "primary" : "text"}
          fontWeight="medium"
          sx={{ pointerEvents: sessionId ? "auto" : "none" }}
        >
          {topicCovered}
        </ArgonTypography>
      ),
      "Tutor Name": (
        <ArgonBox display="flex" flexDirection="column">
          <ArgonTypography variant="caption" fontWeight="medium" color="text">
            {tutorInfo.name}
          </ArgonTypography>
          {tutorInfo.email && (
            <ArgonTypography variant="caption" color="secondary">
              {tutorInfo.email}
            </ArgonTypography>
          )}
        </ArgonBox>
      ),
      Type: (
        <ArgonTypography variant="caption" color="text" fontWeight="medium">
          {latestAttendance?.type || classSession.type || "N/A"}
        </ArgonTypography>
      ),
      Student: (
        <ArgonTypography variant="caption" color="text" fontWeight="medium">
          {getStudentsLabel(students)}
        </ArgonTypography>
      ),
      "Remaining class": <RemainingProgress percentage={remainingPercentage} />,
      "Duration left": (
        <ArgonTypography variant="caption" color="text" fontWeight="medium">
          {progress.remainingClasses} hours
        </ArgonTypography>
      ),
      "class status": (
        <ArgonBadge
          variant="gradient"
          badgeContent={status}
          color={getStatusColor(status)}
          size="xs"
          container
        />
      ),
      "cycle payment status": (
        <ArgonBadge
          variant="gradient"
          badgeContent={paymentStatus}
          color={getPaymentColor(paymentStatus)}
          size="xs"
          container
        />
      ),
      Action: (
        <ArgonBox display="flex" alignItems="center" gap={0}>
          <ArgonButton
            variant="text"
            size="large"
            component={sessionId ? Link : "button"}
            to={sessionId ? `/attendance/profile/${sessionId}` : undefined}
            sx={{ minWidth: "12px", p: 0 }}
            disabled={!sessionId}
          >
            <VisibilityIcon color="primary" />
          </ArgonButton>
          {/* <ArgonButton
            variant="text"
            size="large"
            sx={{ minWidth: "32px", p: 0 }}
            onClick={() => handleEditClick && handleEditClick(latestAttendance)}
            disabled={!latestAttendance || !handleEditClick}
          >
            <EditIcon color="info" />
          </ArgonButton>
          <ArgonButton
            variant="text"
            size="large"
            sx={{ minWidth: "32px", p: 0 }}
            onClick={() => handleDeleteClick(sessionId)}
          >
            <DeleteIcon color="error" />
          </ArgonButton> */}
        </ArgonBox>
      ),
    };
  });

  return {
    columns: [
      { name: "sno", align: "center" },
      { name: "Class", align: "left" },
      { name: "Tutor Name", align: "left" },
      { name: "Type", align: "left" },
      { name: "Student", align: "left" },
      { name: "Remaining class", align: "left" },
      { name: "Duration left", align: "left" },
      { name: "class status", align: "center" },
      { name: "cycle payment status", align: "center" },
      { name: "Action", align: "center" },
    ],
    rows,
  };
};

export default AttendanceTableData;
